import React, { useState } from "react";
import { useTranslation } from "@refinedev/core";
import { useAtomValue } from "jotai";
import { roleAtom } from "../../auth/firebase/state";
import { PRO_ROLES, USER_ROLES } from "../../access/definitions";
import { PlansPopup } from "./PlansPopup";
import { MembershipPopup } from "./MembershipPopup";
import { ServicePopup } from "./ServicePopup";
import { FaPhone, FaCrown, FaArrowRight } from "react-icons/fa6";


interface ServiceOrPlanChoiceProps {
  onUseService: () => void;
  className?: string;
  serviceTitle?: string;
  serviceDescription?: string;
  serviceIcon?: React.ReactNode;
  serviceButtonText?: string;
  serviceType?: 'help' | 'consultation';
}

export const ServiceOrPlanChoice = ({ 
  onUseService,
  className = "",
  serviceTitle,
  serviceDescription,
  serviceIcon,
  serviceButtonText,
  serviceType = 'help'
}: ServiceOrPlanChoiceProps) => {
  const { translate } = useTranslation();
  const { role } = useAtomValue(roleAtom);
  const [showPlans, setShowPlans] = useState(false);
  const [showService, setShowService] = useState(false);
  


  const isUser = USER_ROLES.includes(role);
  const isPro = PRO_ROLES.includes(role);

  if (showService) {
    return <ServicePopup 
      className={className} 
      serviceType={serviceType}
      onBack={() => setShowService(false)}
    />;
  }

  if (showPlans) {
    if (isUser) {
      return <PlansPopup className={className} />;
    }
    if (isPro) {
      return <MembershipPopup className={className} />;
    }
  }

  return (
    <div className={`flex flex-col items-center justify-center min-h-[500px] p-8 bg-base-200 ${className}`}>
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-base-content mb-4">
            {translate("page.helps.options.contactProfessional.title")}
          </h1>
          <p className="text-base-content/70 text-lg">
            {translate("page.serviceChoice.subtitle")}
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Usar Servicio */}
          <div className="card bg-base-100 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
            <div className="card-body p-8 text-center">
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-primary/10 rounded-full">
                  {serviceIcon ? React.cloneElement(serviceIcon as React.ReactElement, { className: "w-12 h-12 text-primary" }) : <FaPhone className="w-12 h-12 text-primary" />}
                </div>
              </div>
              
              <h2 className="text-2xl font-bold text-base-content mb-4">
                {serviceTitle || translate("page.serviceChoice.useService.defaultTitle")}
              </h2>
              
              <p className="text-base-content/70 mb-4 leading-relaxed">
                {serviceDescription || translate("page.serviceChoice.useService.defaultDescription")}
              </p>
              

              
              <button 
                className="btn btn-primary w-full gap-2"
                onClick={() => setShowService(true)}
              >
                {serviceIcon || <FaPhone className="w-4 h-4" />}
                {serviceButtonText || translate("page.serviceChoice.useService.defaultButton")}
              </button>
            </div>
          </div>

          {/* Comprar Plan */}
          <div className="card bg-base-100 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border-2 border-secondary">
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
              <div className="bg-secondary text-secondary-content px-4 py-1 rounded-full text-sm font-semibold">
                {translate("page.serviceChoice.buyPlan.recommended")}
              </div>
            </div>
            
            <div className="card-body p-8 text-center">
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-secondary/10 rounded-full">
                  <FaCrown className="w-12 h-12 text-secondary" />
                </div>
              </div>
              
              <h2 className="text-2xl font-bold text-base-content mb-4">
                {isUser ? translate("page.serviceChoice.buyPlan.planTitle") : translate("page.serviceChoice.buyPlan.membershipTitle")}
              </h2>
              
              <p className="text-base-content/70 mb-6 leading-relaxed">
                {translate("page.serviceChoice.buyPlan.description")}
              </p>
              
              <button 
                className="btn btn-secondary w-full gap-2"
                onClick={() => setShowPlans(true)}
              >
                <FaCrown className="w-4 h-4" />
                {isUser ? translate("page.serviceChoice.buyPlan.viewPlans") : translate("page.serviceChoice.buyPlan.viewMemberships")}
                <FaArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="text-center mt-8">
          <p className="text-sm text-base-content/60">
            {translate("page.serviceChoice.footer")}
          </p>
        </div>
      </div>
    </div>
  );
};